import { isNil } from 'lodash';

// Utils
import {
    getElementType,
    getMediaOnlyDisplay,
    getShowCaption,
} from '../../../common/elements/utils/elementPropertyUtils';
import { getIsLinkMediaVisible } from '../link/linkUtils';
import { getIsFileMediaVisible } from '../file/utils/fileElementUtils';
import { getElementCardWidthGU } from '../utils/elementUtil';
import { propIn } from '../../../common/utils/immutableHelper';

// Constants
import { FILE_ELEMENT_FULL_DISPLAY_MIN_WIDTH } from '../file/fileElementConstants';
import { ELEMENT_FULL_DISPLAY_MIN_WIDTH } from '../../../common/elements/elementConstants';

// Types
import { ImMNElement, MNFileContent } from '../../../common/elements/elementModelTypes';
import { ElementType } from '../../../common/elements/elementTypes';
import { MediaElementContentVisibility, MediaOnlyDisplay } from '../resizing/resizeTypes';
import { RectSize } from '../../../common/maths/geometry/rect/rectTypes';
import { LINK_ELEMENT_FULL_DISPLAY_MIN_WIDTH } from '../link/LinkConstants';

type MediaUtilParams = {
    element: ImMNElement;
    tempSize?: RectSize;
    inList?: string;
    parentWidthGU?: number;
    tempMediaElementVisibility?: MediaElementContentVisibility;
    attachment?: MNFileContent;
};

/**
 * Determines if the media component for a given element is currently being displayed.
 * This includes displaying either the media itself or a placeholder.
 */
export const getIsMediaComponentVisible = (props: MediaUtilParams) => {
    const { element, attachment } = props;

    switch (getElementType(element)) {
        case ElementType.LINK_TYPE:
            return getIsLinkMediaVisible(element);
        case ElementType.FILE_TYPE:
            return getIsFileMediaVisible({ element, attachment });
        default:
            // Other element types either don't have media to show (e.g. Note) or will show a placeholder if no image (e.g. Image)
            return true;
    }
};

export const getFullDisplayMinWidthForElement = (element: ImMNElement): number => {
    switch (getElementType(element)) {
        case ElementType.FILE_TYPE:
            return FILE_ELEMENT_FULL_DISPLAY_MIN_WIDTH;
        case ElementType.LINK_TYPE:
            return LINK_ELEMENT_FULL_DISPLAY_MIN_WIDTH;
        default:
            return ELEMENT_FULL_DISPLAY_MIN_WIDTH;
    }
};

export const getIsElementBelowFullDisplayMinWidth = (props: MediaUtilParams) =>
    getElementCardWidthGU(props) < getFullDisplayMinWidthForElement(props.element);

// Legacy prop for backwards compatibility
const getLegacyShowMediaOnly = propIn(['content', 'showMediaOnly']);

/**
 * Return a boolean value indicating whether media-only display has been set for the given element.
 **/
export const getIsMediaOnlyDisplayPreferred = (element: ImMNElement) => {
    const mediaOnlyDisplay = getMediaOnlyDisplay(element);

    const legacyShowMediaOnly = getLegacyShowMediaOnly(element);
    if (!mediaOnlyDisplay && !isNil(legacyShowMediaOnly)) return legacyShowMediaOnly;

    return mediaOnlyDisplay === MediaOnlyDisplay.TOO_SMALL || mediaOnlyDisplay === MediaOnlyDisplay.PREFERENCE;
};

/**
 * Determines whether we should apply the media-only setting given the current element properties and temporary state.
 */
export const getShouldApplyMediaOnlyDisplay = (props: MediaUtilParams) => {
    if (!getIsMediaComponentVisible(props)) return false;

    if (getIsElementBelowFullDisplayMinWidth(props)) return true;

    const { element, tempMediaElementVisibility } = props;

    switch (tempMediaElementVisibility) {
        case MediaElementContentVisibility.SHOW_MEDIA_ONLY:
            return true;
        case MediaElementContentVisibility.SHOW_ALL:
            return false;
        default:
            return getIsMediaOnlyDisplayPreferred(element);
    }
};

export const getShouldShowElementCaption = (props: MediaUtilParams) =>
    getShowCaption(props.element) && !getShouldApplyMediaOnlyDisplay(props);

export const getShouldShowElementCaptionMediaOptional = (props: MediaUtilParams, isMediaShowing: boolean) => {
    if (isMediaShowing && getShouldApplyMediaOnlyDisplay(props)) return false;

    return getShowCaption(props.element);
};

export const getCanElementToggleInfo = (props: MediaUtilParams): boolean =>
    getIsMediaComponentVisible(props) && !getIsElementBelowFullDisplayMinWidth(props);
